import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { SecurityService } from "../services/security.service";
import { TokenManagerService } from "../services/token-manager.service";
import { PinValidationService } from "../services/pin-validation.service";
import { catchError, switchMap, filter, finalize, take } from 'rxjs/operators';

type RefreshCycle = { logoutTriggered: boolean };
type RefreshResult = { token: string; cycle: RefreshCycle } | { error: unknown };

/**
 * AuthInterceptor - Intercepta peticiones HTTP para agregar JWT
 * Responsabilidades:
 * 1. Agregar JWT a peticiones HTTP
 * 2. Agregar Refresh Token a peticiones de renovación
 * 3. Renovar JWT automáticamente en caso de error 401
 * 4. Gestionar MFA requerido por el servidor sin cerrar sesión
 * 5. Redirigir a login si no se puede renovar
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<RefreshResult | null>(null);
    private currentRefreshCycle: RefreshCycle | null = null;

    constructor(
        private authService: SecurityService, 
        private router: Router,
        private tokenManager: TokenManagerService,
        private pinValidationService: PinValidationService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        req = this.addAuthSessionVersionHeader(req);

        // 0. Archivos de traducción i18n - NUNCA agregar autenticación
        if (this.getPathname(req.url).startsWith('/assets/i18n/')) {
            return next.handle(req);
        }
        
        // 1. Si la petición tiene el header X-Skip-Interceptor, no agregar autenticación
        if (req.headers.has('X-Skip-Interceptor')) {
            const cloned = req.clone({
                headers: req.headers.delete('X-Skip-Interceptor')
            });
            return next.handle(cloned);
        }
        
        // 2. Para /auth/refresh, añadir el refresh token en el header Authorization
        if (this.getPathname(req.url) === '/auth/refresh') {
            return this.handleRefreshRequest(req, next);
        }
        
        // 3. Excluir rutas públicas que no requieren autenticación
        if (this.isPublicRoute(req.url)) {
            return next.handle(req);
        }

        // 4. Agregar JWT a la petición
        return this.addTokenToRequest(req, next);
    }

    private addAuthSessionVersionHeader(request: HttpRequest<any>): HttpRequest<any> {
        if (!this.getPathname(request.url).startsWith('/auth/')) {
            return request;
        }
        return request.clone({
            headers: request.headers.set('X-Auth-Session-Version', '2')
        });
    }

    private getPathname(url: string): string {
        try {
            return new URL(url, window.location.origin).pathname;
        } catch {
            const delimiterIndexes = [url.indexOf('?'), url.indexOf('#')]
                .filter(index => index >= 0);
            const pathnameEnd = delimiterIndexes.length > 0
                ? Math.min(...delimiterIndexes)
                : url.length;
            return url.slice(0, pathnameEnd);
        }
    }

    /**
     * Maneja peticiones a /auth/refresh agregando el refresh token
     */
    private handleRefreshRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const refreshToken = this.authService.getRefreshToken();
        let headers = req.headers;
        if (refreshToken) {
            headers = headers.set('Authorization', 'Bearer ' + refreshToken);
        }

        return next.handle(req.clone({ headers, withCredentials: true })).pipe(
            catchError(error => {
                if (this.handleMfaRequiredError(error, req.url)) {
                    return throwError(() => error);
                }
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    this.authService.logout();
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Verifica si la ruta es pública (no requiere autenticación)
     */
    private isPublicRoute(url: string): boolean {
        const path = this.getPathname(url);

        if (path.startsWith('/assets/')) {
            return true;
        }

        const publicEndpoints = [
            '/auth/login',
            '/auth/passkey/login/options',
            '/auth/passkey/login',
            '/auth/register',
            '/auth/logout',
            '/auth/validate-pin',
            '/auth/resend-pin',
            '/billcentrix/V2/resetPassword',
            '/billcentrix/V2/user',
            '/log'
        ];

        if (publicEndpoints.includes(path)) {
            return true;
        }

        return ['/token', '/oauth/token', '/auth/token'].includes(path.toLowerCase());
    }

    /**
     * Verifica si la ruta NO debe intentar renovar el token en caso de 401
     * Estas rutas pueden devolver 401 por razones de negocio (ej: contraseña incorrecta)
     */
    private shouldNotRetryOn401(url: string): boolean {
        const noRetryRoutes = [
            '/changePassword',
            '/validate-pin'
        ];
        
        return noRetryRoutes.some(route => url.includes(route));
    }

    /**
     * Agrega el JWT a la petición y maneja errores 401
     * Usa TokenManagerService para renovación proactiva (60 seg antes de expirar)
     */
    private addTokenToRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.authService.getToken();

        if (!token) {
            return this.handleNoAuth();
        }

        if (this.tokenManager.isTokenExpiringSoon()) {
            return this.handle401Error(req, next);
        }

        const cloned = this.addAccessTokenHeaders(req, token);

        return next.handle(cloned).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    if (this.shouldNotRetryOn401(req.url)) {
                        return throwError(() => error);
                    }
                    return this.handle401Error(req, next);
                }
                if (this.handleMfaRequiredError(error, req.url)) {
                    return throwError(() => error);
                }
                return throwError(() => error);
            })
        );
    }

    private addAccessTokenHeaders(request: HttpRequest<any>, token: string): HttpRequest<any> {
        const user = this.authService.getTokenUser();
        let headers = request.headers.set('Authorization', 'Bearer ' + token);
        if (user !== null && user !== undefined) {
            headers = headers.set('User', user.toString());
        }
        return request.clone({ headers });
    }

    private handleMfaRequiredError(error: unknown, requestUrl: string): boolean {
        if (!(error instanceof HttpErrorResponse) ||
            error.status !== 403 ||
            error.error?.error !== 'MFA_REQUIRED') {
            return false;
        }

        if (this.isPasskeyRevalidationRoute(requestUrl)) {
            return true;
        }

        this.authService.isPinValidated(false);
        if (!this.pinValidationService.isPinModalVisible()) {
            this.pinValidationService.showPinModal();
        }
        return true;
    }

    private isPasskeyRevalidationRoute(url: string): boolean {
        const path = this.getPathname(url);
        return path === '/auth/passkey/revalidate/options' ||
            path === '/auth/passkey/revalidate';
    }

    /**
     * Maneja el caso cuando no hay autenticación válida
     */
    private handleNoAuth(): Observable<HttpEvent<any>> {
        const currentUrl = this.router.url;
        const isPublicPage = currentUrl.includes('/login') || 
                             currentUrl.includes('/create') || 
                             currentUrl.includes('/reset') ||
                             currentUrl === '/' ||
                             currentUrl === '';
        
        if (!isPublicPage) {
            this.authService.logout();
        }
        
        return throwError(() => new Error('No hay autenticación válida'));
    }

    /**
     * Maneja errores 401 renovando la sesión (refresh JS o cookie HttpOnly)
     */
    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Si ya hay una renovación en proceso, esperar a que termine
        if (this.isRefreshing) {
            return this.waitForTokenRefresh(request, next);
        }

        // Iniciar proceso de renovación
        this.isRefreshing = true;
        const refreshCycle: RefreshCycle = { logoutTriggered: false };
        this.currentRefreshCycle = refreshCycle;
        this.refreshTokenSubject.next(null);
        let refreshSettled = false;

        return this.authService.refreshToken().pipe(
            catchError((error) => {
                refreshSettled = true;
                this.isRefreshing = false;
                if (this.handleMfaRequiredError(error, request.url)) {
                    this.clearRefreshCycle(refreshCycle);
                    this.notifyRefreshFailure(error);
                    return throwError(() => error);
                }
                this.logoutRefreshCycleOnce(refreshCycle);
                this.clearRefreshCycle(refreshCycle);
                this.notifyRefreshFailure(error);
                return throwError(() => error);
            }),
            switchMap((response: any) => {
                if (response && response.jwt) {
                    refreshSettled = true;
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next({ token: response.jwt, cycle: refreshCycle });

                    const cloned = this.addAccessTokenHeaders(request, response.jwt);
                    return next.handle(cloned).pipe(
                        catchError((error) => this.handleRetriedRequestError(error, refreshCycle, request.url))
                    );
                }

                const error = new Error('No se pudo renovar el token');
                refreshSettled = true;
                this.isRefreshing = false;
                this.logoutRefreshCycleOnce(refreshCycle);
                this.clearRefreshCycle(refreshCycle);
                this.notifyRefreshFailure(error);
                return throwError(() => error);
            }),
            finalize(() => {
                if (!refreshSettled) {
                    refreshSettled = true;
                    this.isRefreshing = false;
                    this.clearRefreshCycle(refreshCycle);
                    this.notifyRefreshFailure(new Error('Renovación cancelada'));
                }
            })
        );
    }

    private notifyRefreshFailure(error: unknown): void {
        this.refreshTokenSubject.next({ error });
    }

    private handleRetriedRequestError(
        error: unknown,
        refreshCycle: RefreshCycle,
        requestUrl: string
    ): Observable<never> {
        if (this.handleMfaRequiredError(error, requestUrl)) {
            return throwError(() => error);
        }
        if (error instanceof HttpErrorResponse && error.status === 401) {
            this.logoutRefreshCycleOnce(refreshCycle);
        }
        return throwError(() => error);
    }

    private logoutRefreshCycleOnce(refreshCycle: RefreshCycle): void {
        if (this.currentRefreshCycle !== refreshCycle || refreshCycle.logoutTriggered) {
            return;
        }
        refreshCycle.logoutTriggered = true;
        this.authService.logout();
    }

    private clearRefreshCycle(refreshCycle: RefreshCycle): void {
        if (this.currentRefreshCycle === refreshCycle) {
            this.currentRefreshCycle = null;
        }
    }

    /**
     * Espera a que termine una renovación de token en proceso
     */
    private waitForTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.refreshTokenSubject.pipe(
            filter((result): result is RefreshResult => result !== null),
            take(1),
            switchMap((result) => {
                if ('error' in result) {
                    this.handleMfaRequiredError(result.error, request.url);
                    return throwError(() => result.error);
                }
                const cloned = this.addAccessTokenHeaders(request, result.token);
                return next.handle(cloned).pipe(
                    catchError((error) => this.handleRetriedRequestError(error, result.cycle, request.url))
                );
            })
        );
    }
}
